import React from "react";
import {
  Alert,
  Button,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";

const COPY_ERRORS_FALLBACK_MESSAGE = "No pudimos copiar los errores en este navegador. Descargá el CSV para conservarlos.";

function csvCell(value) {
  const text = String(value ?? "");
  return `"${text.replace(/"/g, '""')}"`;
}

function errorsCsv(errors = []) {
  const lines = [
    ["fila", "campo", "fase", "mensaje"],
    ...errors.map((error) => [error.row || "", error.field || "", error.phase || "", error.message || ""]),
  ];
  return lines.map((line) => line.map(csvCell).join(",")).join("\n");
}

function downloadErrors(errors) {
  const blob = new Blob(["\uFEFF", errorsCsv(errors)], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "webprofit-importacion-errores.csv";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function Summary({ label, value }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, flex: "1 1 130px" }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 950 }}>{Number(value || 0).toLocaleString("es-AR")}</Typography>
    </Paper>
  );
}

export default function ImportResultStep({ preview, result, busy, onCommit }) {
  const previewTotals = preview?.totals || {};
  const totals = result?.totals || {};
  const errors = Array.isArray(result?.errors) ? result.errors : [];
  const movements = Array.isArray(result?.stock?.movements) ? result.stock.movements : [];
  const [copied, setCopied] = React.useState(false);
  const [copyError, setCopyError] = React.useState("");
  const copyFeedbackTimerRef = React.useRef(null);

  const clearCopyFeedbackTimer = React.useCallback(() => {
    if (copyFeedbackTimerRef.current) {
      window.clearTimeout(copyFeedbackTimerRef.current);
      copyFeedbackTimerRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    clearCopyFeedbackTimer();
    setCopied(false);
    setCopyError("");
  }, [result, clearCopyFeedbackTimer]);

  React.useEffect(() => () => {
    clearCopyFeedbackTimer();
  }, [clearCopyFeedbackTimer]);

  const copyErrors = async () => {
    if (!errors.length) return;

    clearCopyFeedbackTimer();
    setCopied(false);
    setCopyError("");

    const writeText = typeof navigator !== "undefined" ? navigator.clipboard?.writeText : null;
    if (typeof writeText !== "function") {
      setCopyError(COPY_ERRORS_FALLBACK_MESSAGE);
      return;
    }

    try {
      await navigator.clipboard.writeText(errorsCsv(errors));
      setCopied(true);
      copyFeedbackTimerRef.current = window.setTimeout(() => {
        setCopied(false);
        copyFeedbackTimerRef.current = null;
      }, 1800);
    } catch {
      setCopyError(COPY_ERRORS_FALLBACK_MESSAGE);
    }
  };

  if (!result) {
    return (
      <Stack spacing={2.5}>
        <div>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>Confirmación</Typography>
          <Typography variant="body2" color="text.secondary">
            {preview?.context === "supplier"
              ? "El backend usará exactamente la selección, mapping y actualizaciones aceptadas guardadas por la última preview."
              : "El backend usará exactamente el mapping/defaults guardado por la última preview. Este paso no envía un mapping alternativo."}
          </Typography>
        </div>
        <Alert severity="info">
          {preview?.context === "supplier"
            ? `Se crearán ${previewTotals.created || 0} ítem(s) y se actualizarán ${previewTotals.updated || 0} existente(s) que aceptaste explícitamente.`
            : `Se intentarán ${previewTotals.created || 0} alta(s) y ${previewTotals.updated || 0} actualización(es). Las ${previewTotals.skipped || 0} fila(s) inválidas se omitirán con detalle en el resultado.`}
        </Alert>
        {preview?.stockPolicy?.mapped ? (
          <Alert severity="warning">
            El stock se conciliará mediante movimientos ERP confirmados e idempotentes; nunca se escribirá directo en el catálogo.
          </Alert>
        ) : null}
        <Button variant="contained" onClick={onCommit} disabled={busy}>
          {busy
            ? "Confirmando…"
            : preview?.context === "supplier"
              ? `Crear ${previewTotals.created || 0} y actualizar ${previewTotals.updated || 0}`
              : "Confirmar importación"}
        </Button>
      </Stack>
    );
  }

  const failed = result.status === "failed";
  return (
    <Stack spacing={2.5}>
      <div>
        <Typography variant="h6" sx={{ fontWeight: 900 }}>Resultado de la importación</Typography>
        <Typography variant="body2" color="text.secondary">
          {result.replayed ? "Este resultado fue recuperado del mismo commit idempotente." : "El commit terminó y el resumen quedó persistido para retries del mismo importId."}
        </Typography>
      </div>

      <Alert severity={failed ? "error" : result.completedWithErrors ? "warning" : "success"}>
        {failed
          ? (result.globalError?.message || "El commit falló y no se volverá a ejecutar automáticamente con este importId.")
          : result.completedWithErrors
            ? "La importación terminó con filas omitidas o incidencias informadas abajo."
            : "Importación completada correctamente."}
      </Alert>

      <Stack direction="row" flexWrap="wrap" gap={1.25}>
        <Summary label="Creados" value={totals.created} />
        <Summary label="Actualizados" value={totals.updated} />
        <Summary label="Omitidos" value={totals.skipped} />
        <Summary label="Errores" value={totals.errors} />
        <Summary label="Mov. stock" value={totals.stockMovements} />
      </Stack>

      {preview?.stockPolicy?.mapped ? (
        <Alert severity={Number(result?.stock?.failedRows || 0) ? "warning" : "success"}>
          Stock: {Number(result?.stock?.adjustedRows || 0)} fila(s) conciliadas en {movements.length} movimiento(s) ERP; {Number(result?.stock?.failedRows || 0)} fila(s) sin aplicar.
        </Alert>
      ) : null}

      {movements.length ? (
        <Stack spacing={0.5}>
          <Typography sx={{ fontWeight: 900 }}>Movimientos ERP generados</Typography>
          {movements.map((movement) => (
            <Typography variant="body2" key={movement.id || movement.movementNumber}>
              {movement.movementNumber || movement.id} · {movement.lines || 0} línea(s)
            </Typography>
          ))}
        </Stack>
      ) : null}

      {errors.length ? (
        <>
          <Divider />
          <Stack spacing={1}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <Button startIcon={<ContentCopyRoundedIcon />} variant="outlined" onClick={copyErrors}>
                {copied ? "Errores copiados" : "Copiar errores CSV"}
              </Button>
              <Button startIcon={<DownloadRoundedIcon />} variant="outlined" onClick={() => downloadErrors(errors)}>
                Descargar errores CSV
              </Button>
            </Stack>
            {copyError ? (
              <Alert severity="warning" role="alert">
                {copyError}
              </Alert>
            ) : null}
          </Stack>
          <TableContainer sx={{ maxHeight: 300, border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 900 }}>Fila</TableCell>
                  <TableCell sx={{ fontWeight: 900 }}>Campo</TableCell>
                  <TableCell sx={{ fontWeight: 900 }}>Fase</TableCell>
                  <TableCell sx={{ fontWeight: 900 }}>Mensaje</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errors.map((error, index) => (
                  <TableRow key={`${error.row}-${error.field}-${error.phase}-${index}`}>
                    <TableCell>{error.row || "—"}</TableCell>
                    <TableCell>{error.field || "—"}</TableCell>
                    <TableCell>{error.phase || "—"}</TableCell>
                    <TableCell>{error.message}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : null}
    </Stack>
  );
}
